import { Button } from "@/components/ui/button";
import { type BotMcpTokenItem, agentService } from "@/services/agent.service/agent.api";
import { CheckCircle2, Copy, Loader2, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const MCP_URL = "https://mcp.baitaticket.com.br/mcp";

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Não foi possível copiar");
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 rounded-md border border-border p-2 transition-colors hover:bg-accent"
      aria-label="Copiar"
    >
      {copied ? (
        <CheckCircle2 className="h-4 w-4 text-green-500" />
      ) : (
        <Copy className="h-4 w-4 text-muted-foreground" />
      )}
    </button>
  );
}

function formatDate(value?: string) {
  if (!value) return "nunca usado";
  return new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

interface BotMcpTokensProps {
  agentId: string;
}

/** Gerenciamento de token MCP de um agente-bot — bot não loga sozinho, então quem gera/revoga é um admin,
 * direto no form do agente (mesmo padrão de McpSettings.tsx, mas em nome de outro usuário). */
export default function BotMcpTokens({ agentId }: BotMcpTokensProps) {
  const [tokens, setTokens] = useState<BotMcpTokenItem[] | null>(null);
  const [freshCommand, setFreshCommand] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const loadTokens = useCallback(async () => {
    const [data, err] = await agentService.listBotMcpTokens(agentId);
    if (err || !data) {
      setTokens([]);
      return;
    }
    setTokens(data.filter((t) => !t.revoked));
  }, [agentId]);

  useEffect(() => {
    void loadTokens();
  }, [loadTokens]);

  async function handleGenerate() {
    setGenerating(true);
    const [data, err] = await agentService.createBotMcpToken(agentId);
    setGenerating(false);
    if (err || !data) {
      toast.error(err?.msg || "Não foi possível gerar o token");
      return;
    }
    const command = `claude mcp add --transport http baitaticket ${MCP_URL} --header "Authorization: Bearer ${data.token}"`;
    setFreshCommand(command);
    await loadTokens();
  }

  async function handleRevoke(id: string) {
    setRevokingId(id);
    const [, err] = await agentService.revokeBotMcpToken(agentId, id);
    setRevokingId(null);
    if (err) {
      toast.error(err?.msg || "Não foi possível revogar o token");
      return;
    }
    toast.success("Token revogado");
    await loadTokens();
  }

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div>
        <h3 className="font-semibold text-sm">Token MCP do bot</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Usado pra conectar este bot em Claude Code/Desktop (ou outro cliente MCP) com as
          permissões deste agente.
        </p>
      </div>

      {freshCommand ? (
        <>
          <div className="flex items-start gap-2">
            <pre className="flex-1 overflow-x-auto whitespace-pre-wrap break-all rounded-md border border-border bg-muted px-3 py-2 text-xs font-mono">
              {freshCommand}
            </pre>
            <CopyButton value={freshCommand} />
          </div>
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Copie agora — o token não aparece de novo depois que você sair daqui.
            </p>
          </div>
        </>
      ) : (
        <Button type="button" size="sm" className="gap-2" onClick={handleGenerate} disabled={generating}>
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Gerar token e comando
        </Button>
      )}

      {tokens === null ? (
        <p className="text-xs text-muted-foreground">Carregando…</p>
      ) : tokens.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nenhum token ativo ainda.</p>
      ) : (
        <div className="space-y-2">
          {tokens.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
            >
              <div className="min-w-0">
                <code className="text-xs font-mono">{t.tokenPrefix}…</code>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Criado em {formatDate(t.createdAt)} · Último uso: {formatDate(t.lastUsedAt)}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 gap-1.5 text-destructive hover:text-destructive"
                onClick={() => handleRevoke(t.id)}
                disabled={revokingId === t.id}
              >
                {revokingId === t.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
                Revogar
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
